import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { ACTIONS, convertKeysToCamelCase } from '../utils/financialHelper';
import { dataAll, dataCreate, dataDelete, dataEdit } from '../service/data.service';

const useCrud = (
  endpoint,
  defaultValues,
  funcionalidade,
  customDefaultButtonLabel = 'Adicionar',
  fetchResponseProcessor = convertKeysToCamelCase,
  handleCloseAdditional = () => {},
  handleUploadFile = async () => {},
  defaultAction = ACTIONS.ADD,
  handleAdditionalValues = (v) => v
) => {
  const [items, setItems] = useState(Array.isArray(defaultValues) ? defaultValues : [defaultValues]);
  const [title, setTitle] = useState(`${customDefaultButtonLabel} ${funcionalidade}`);
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState(defaultAction);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [values, setValues] = useState(defaultValues);
  const [filterValue, setFilterValue] = useState('');
  const [dadoFoiModificado, setDadoFoiModificado] = useState(false);
  const [buttonLabel, setButtonLabel] = useState(customDefaultButtonLabel);

  const isMounted = useRef(true); // Função para evitar memory leak, assíncrono com componente desmontado

  const fetchAllItems = async () => {
    try {
      const response = await dataAll(endpoint);

      if (isMounted.current) {
        setItems(fetchResponseProcessor(response));
      }
    } catch (error) {
      if (isMounted.current) {
        toast.error(
          `Erro ao buscar ${funcionalidade}. Favor tentar novamente dentro de alguns instantes. Sinalize o suporte caso persista. Erro técnico: ${error}`
        );
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    isMounted.current = true;
    fetchAllItems();

    return () => {
      isMounted.current = false;
    };
  }, [endpoint, funcionalidade]);

  const reset = () => {
    setTitle(`${customDefaultButtonLabel} ${funcionalidade}`);
    setAction(defaultAction);
    setButtonLabel(customDefaultButtonLabel);
    setValues(defaultValues);
  };

  const handleOpen = () => {
    setOpen(true);
    setFilterValue('');
  };

  const handleClose = () => {
    handleCloseAdditional();
    setOpen(false);
    reset();
  };

  const handleView = (id) => {
    const itemSelected = items.find((item) => item.id === id);
    if (!itemSelected) return;

    setValues(itemSelected);
    setAction(ACTIONS.VIEW);
    setTitle(`Visualizar ${funcionalidade}`);
    setButtonLabel('Fechar');
    handleOpen();
  };

  const handleEdit = (id) => {
    const itemSelected = items.find((item) => item.id === id);
    if (!itemSelected) return;

    setValues(itemSelected);
    setAction(ACTIONS.EDIT);
    setTitle(`Editar ${funcionalidade}`);
    setButtonLabel('Editar');
    handleOpen();
  };

  const handleDelete = (id) => {
    const itemSelected = items.find((item) => item.id === id);
    if (!itemSelected) return;

    setValues(itemSelected);
    setAction(ACTIONS.DELETE);
    setTitle(`Excluir ${funcionalidade}`);
    setButtonLabel('Excluir');
    handleOpen();
  };

  const handleClone = (id) => {
    const itemSelected = items.find((item) => item.id === id);
    if (!itemSelected) return;

    setValues(itemSelected);
    setTitle(`Duplicar ${funcionalidade}`)
    setButtonLabel('Salvar');
    handleOpen();
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingSubmit(true);

    let apiResponse;
    switch (action) {
      case ACTIONS.ADD:
        apiResponse = await dataCreate(endpoint, values);
        if (apiResponse) {
          toast.success(`${funcionalidade} adicionado(a) com sucesso!`);
          const postProcessedValues = handleAdditionalValues(values);
          setItems([...items, { ...postProcessedValues, id: apiResponse.id }]);
        }
        break;

      case ACTIONS.EDIT:
        apiResponse = await dataEdit(endpoint, values, values.id);
        if (apiResponse) {
          toast.success(`${funcionalidade} editado(a) com sucesso!`);
          const postProcessedValues = handleAdditionalValues(values);
          setItems(items.map((item) => (item.id === postProcessedValues.id ? postProcessedValues : item)));
        }
        break;

      case ACTIONS.VIEW:
        break;

      case ACTIONS.DELETE:
        apiResponse = await dataDelete(endpoint, values.id);
        if (apiResponse) {
          toast.success(`${funcionalidade} excluído(a) com sucesso!`);
          setItems(items.filter((item) => item.id !== values.id));
        }
        break;

      case ACTIONS.UPLOAD:
        apiResponse = await handleUploadFile();
        if (apiResponse) {
          toast.success(`${funcionalidade} enviado(a) com sucesso!`);
          await fetchAllItems();
        }
        break;

      default:
        throw new Error('Escolha uma ação válida dentre as constantes.');
    }

    setIsLoadingSubmit(false);
    setDadoFoiModificado(true);
    handleClose();
  };

  return {
    action,
    buttonLabel,
    dadoFoiModificado,
    filterValue,
    isLoading,
    isLoadingSubmit,
    items,
    open,
    title,
    values,
    setAction,
    setButtonLabel,
    setDadoFoiModificado,
    setFilterValue,
    setItems,
    setValues,
    handleClose,
    handleDelete,
    handleEdit,
    handleOpen,
    handleSubmit,
    handleView,
    handleClone,
  };
};

export default useCrud;
